// provide abstraction interface for the webSocket
import { BaseSocket, SocketSurrogate, SotiWebSocket } from '../webSocket';
import { CloseHandler, ConnectHandler, ErrorHandler, IInitAuthSession, IInitRequestPacket, MessageType, SignatureByte, SotiAssistMessageType } from 'app/modules/remote-control-ng/definitions';
import { MessageHandler } from '../messageHandler';
import { Injectable, Injector } from '@angular/core';
import { BaseMessage } from '../messages';
import { RcLoggerService } from './rcLoggerService';
import { StartRemoteControlMessage } from '../messages/com/startRemoteControlMessage';
import { RequestReplyService } from './requestReplyService';
import { AdminCommandTypes, AdminMessage } from '../messages/pocket-com';
import { Observable } from 'rxjs';
import { CallbackType, IRemoteControlConnection } from '../mcrc-api';
import { RcContext } from '../context/rcContext';
import { VideoRecordingSetupMessage } from '../messages/xsight';

@Injectable({
    providedIn: "root"
})
export class ConnectionBridge {
    private lastError: number = 0;
    private socketInst: BaseSocket = null;
    private messageSentEvent: any;
    private messageHandler: MessageHandler;

    // TODO 2025.1 XS-23332: integrate NgRemoteControlConnection video streaming code into the ConnectionBridge and remove attach
    // in case of video streaming, socket connection used to be established by NgRemoteControlConnection, not mcrc.dal.socketBridge.
    // mcrc.dal.socketBridge.attach() was called so that socketBridge works like a proxy, and file manager, task manager and other modules can work with NgRemoteControlConnection without change.
    // for the messages sent to RC Service (with signature 0xD7, such as server side video recording commands), we must create a socket connection to RC Service,
    // adding variable recordingServer is for this purpose.
    //
    private recordingServer: SotiWebSocket = null;
    private recordingSkinConfiguration: VideoRecordingSetupMessage = null;
    private recordingServiceUri: string;
    private getRemoteControlToken: () => Promise<IInitAuthSession>;
    private connectionPromise: any = null;
    private startRecordingSent: boolean;

    private _callbackPreregistrationClose: CloseHandler;
    private _callbackPreregistrationConnect: ConnectHandler;
    private _callbackPreregistrationError: ErrorHandler;

    protected static currentReqId: number = 0;

    constructor(
        private mcrcLog: RcLoggerService,
        private requestService: RequestReplyService,
        private injector: Injector,
    ) { }

    static constructInitKeyString(
        recordingService: boolean,
        scale: number,
        ticketId?: number | string,
        deviceName?: string,
        fps?: number,
        proxy?: string,
    ): string {
        // first param is different for streaming and non streaming RC. Streaming should not receive the TicketId,
        // that should be sent via the admin message
        const firstParam = fps == null ? `TicketId=${ticketId || ''}` : `fps=${fps}`;
        // for video streaming recording
        const recordingParam = recordingService ? ";RecordingService=1" : "";
        const scaleParam = Number(scale) > 0 ? `;Zoom=${scale}` : "";
        const proxyParam = proxy == null ? "" : `;Hosts=${proxy}`;

        //
        // JL: RC Service copies 'Bpp' into context.ConnectionFlags directly, so that Bpp should be defined as PKK_16BPP_REDUCTION (0x0800), not mcrc.consts.ColorQuality.ColorQuality16Bpp (0x0008)
        // 0x0008 is defined as PkkFlags.PkkVideoCodec which tells agent that video streaming is supported by server.
        //
        return `${firstParam};DisplayName=${deviceName ? deviceName.split('[')[0] : ''}${recordingParam}${scaleParam}${proxyParam}`;
    }


    private dispatchMessageSentEvent(messageType) {
        this.messageSentEvent = document.createEvent('CustomEvent');
        this.messageSentEvent.initCustomEvent('socketMessageSent', true, true, messageType);

        document.dispatchEvent(this.messageSentEvent);
    }

    private setupSocket = (isStreaming: boolean) => {
        if (!this.messageHandler) {
            this.messageHandler = new MessageHandler(this.injector, undefined, isStreaming);
            this.socketInst.messageHandler = this.messageHandler;
            if (this._callbackPreregistrationClose) {
                this.subscribeCloseHandler(this._callbackPreregistrationClose)
                this._callbackPreregistrationClose = null;
            }
            if (this._callbackPreregistrationConnect) {
                this.subscribeOpenHandler(this._callbackPreregistrationConnect)
                this._callbackPreregistrationConnect = null;
            }
        }
    };

    public subscribeOpenHandler(callback: ConnectHandler): void {
        if (this.messageHandler) {
            this.messageHandler.setOnConnect(callback);
            this.mcrcLog.debug('Socket callback registered for open event');
        } else {
            this._callbackPreregistrationConnect = callback;
        }
    };

    public unsubscribeOpenHandler(): void {
        this.messageHandler.setOnConnect(null);
        this._callbackPreregistrationConnect = null;
    }

    public subscribeCloseHandler(callback: CloseHandler): void {
        if (this.messageHandler) {
            this.messageHandler.setOnClose(callback);
            this.mcrcLog.debug('Socket callback registered for close event');
        } else {
            this._callbackPreregistrationClose = callback;
        }
    };

    public clearMessageSubscriptions = () => {
        this._callbackPreregistrationClose = null;
        this._callbackPreregistrationConnect = null;
        this._callbackPreregistrationError = null;
        if (this.socketInst) this.socketInst.messageHandler = null;
        if (this.recordingServer) this.recordingServer.messageHandler = null;
        this.messageHandler = null;
    };

    public setRecordingSkinConfiguration = (skinConfiguration: BaseMessage) => {
        if (skinConfiguration instanceof VideoRecordingSetupMessage) {
            this.recordingSkinConfiguration = skinConfiguration;
        } else {
            throw new Error("Invalid video recording skin configuration type");
        }
    };

    public init = (settings: any) => {
        this.mcrcLog.info('socketBridge.init - settings: ' + JSON.stringify(settings));
        this.socketInst = new SotiWebSocket(this.mcrcLog, settings.provider);
        this.recordingServiceUri = null;
        this.setupSocket(false);
        this.socketInst.connect(settings.host, "mobicontrol-rc-v1", true);
    };

    // video streaming - attach web socket (DS connection) for modules like file manager and task manager who uses mcrc.dal.socketBridge
    public attach = (connection: IRemoteControlConnection) => {
        this.socketInst = new SotiWebSocket(this.mcrcLog);
        this.setupSocket(true);
        this.socketInst.attach(connection.getWebSocket());
    };

    // video streaming - initialize recording service
    public initRecordingService = (uri: string, cb: () => Promise<IInitAuthSession>) => {
        this.recordingServiceUri = uri;
        this.getRemoteControlToken = cb;
    };

    /** Socket transfer has 3 modes: transfer within the same tab from standalone to in ticket, no socket
     * change is necessary. Transfer from the main window to popout is executed in the popout instance to
     * create the SocketSurrogate and connect it to the main instance. And popin transfer from the popout
     * into main that is called in both instances
     */
    public transfer = (oldSocketInst: BaseSocket) => {
        /** main => main: transfer called on the same instance */
        const withinWindow = oldSocketInst.window === window;
        /** main => popout: accepting popout in the popout instance */
        const toPopoutWnd = !this.socketInst;
        this.mcrcLog.debug(`ConnectionBridge transfer - same instance: ${withinWindow} popout starting: ${toPopoutWnd}`);

        if (withinWindow) {
            return;
        }

        // for video streaming, socketInst has been initialized by attach(), and postMessage is not used (send directly on old web socket)
        if (toPopoutWnd) {
            this.socketInst = new SocketSurrogate();
            this.socketInst.targetWindow = oldSocketInst.window;
            this.setupSocket(RcContext.instance.isStreaming);
            this.socketInst.attach(oldSocketInst);
        }
        const inPopout = this.socketInst instanceof SocketSurrogate;
        const inMain = this.socketInst instanceof SotiWebSocket;

        if (inPopout && toPopoutWnd) {
            // Set the target window property of the socket.
            // SocketBridge runs in the new (popout) window, whereas WebSocket remains in the original window
            (oldSocketInst as SotiWebSocket).registerSocketProxy()
            oldSocketInst.targetWindow = window;
        } else if (inMain) {
            // We are transferring back to the original window, so clear the socket's target window
            (this.socketInst as SotiWebSocket).unregisterSocketProxy();
            this.socketInst.targetWindow = null;
        }
    };

    public sendRequest = (msg: BaseMessage): Observable<BaseMessage> => {
        msg.setRequestId(++ConnectionBridge.currentReqId);
        const request = this.requestService.registerMessage(msg.reqId);
        this.send(msg);
        return request;
    };

    public sendCallbackRequest = (msg: BaseMessage, callback?: CallbackType, fallback?: CallbackType): number => {
        msg.setRequestId(++ConnectionBridge.currentReqId);
        if (callback) {
            this.requestService.callbacks.set(msg.reqId, callback);
            this.requestService.fallbacks.set(msg.reqId, fallback);
        }
        this.send(msg);
        return msg.reqId;
    };

    /** sends a BaseMessage */
    public send = (message: BaseMessage) => {
        const msg = <BaseMessage>message;
        this.dispatchMessageSentEvent(msg.command);

        if (this.socketInst) {
            if (window != this.socketInst.window) {
                const messageData: Uint8Array = !msg ? undefined : msg.serialize();
                this.socketInst.send(messageData);
            } else if (msg) {
                // video streaming - send Soti Assist messages (recording, log upload etc) to RC Service
                if (this.recordingServiceUri && (msg.signature === SignatureByte.SignatureSotiAssistMessage
                    || msg.command === MessageType.PC_ADMIN
                    || msg.command === MessageType.PC_AGENT_RECORD
                    || msg.command === MessageType.PC_AGENT_DUMP_LOG)) {
                    this.sendToRcService(msg);
                } else {
                    const msgData = msg.serialize();
                    this.socketInst.send(msgData);
                    if (msg.command === MessageType.MOUSE && this.recordingServer && this.recordingServer.isConnected()) {
                        this.recordingServer.send(msgData);
                    }
                }
            }
        }
    };

    // video streaming - send Soti Assist messages to RC Service
    private sendToRcService = async (msg: BaseMessage) => {
        // handle DS bug, when 2nd SAC_VIDEO_RECORDING_START is sent to RC Service, and RC Service sends PC_RESTART to DS,
        // DS might not call NewStreamOnNextKeyFrame() to generate a new MP4 stream for the next key frame.
        // fix by making RC Service creating a new connection to DS for the 2nd SAC_VIDEO_RECORDING_START
        const sig = msg.signature;
        const cmd = msg.command;
        // when a standalone RC is transferred to a ticket, ticket ID needs to be sent to the RC service to handle
        // the differences in processing of video and log dumps. Use this channel and reopen it if needed,
        // making sure that startRecordingSent is not affected by teh PC_ADMIN message
        const isNewTicket = (cmd === MessageType.PC_ADMIN && (msg as AdminMessage).commandType === AdminCommandTypes.helpDeskSetTicketId);
        if ((sig === SignatureByte.SignatureSotiAssistMessage && cmd === SotiAssistMessageType.SAC_VIDEO_RECORDING_START) || isNewTicket) {
            if (this.startRecordingSent && this.recordingServer && this.recordingServer.isConnected()) {
                this.recordingServer.close();
                this.recordingServer = null;
                this.connectionPromise = null
            }

            this.startRecordingSent = !isNewTicket;
        }

        if (this.recordingServer && this.recordingServer.isConnected()) {
            this.recordingServer.send(msg.serialize());
            return;
        }

        if (!this.connectionPromise) {
            this.connectionPromise = new Promise(async resolve => {
                const session = this.getRemoteControlToken ? await this.getRemoteControlToken() :
                    {
                        token: RcContext.instance.rcToken,
                        sessionId: RcContext.instance.sessionId
                    };
                // create a new instance of the websocket, wait for the connection to be established
                // and RC initialized before resolving to continue and send the message
                this.createRecordingSocket(session, resolve);
            });
        }

        await this.connectionPromise;
        this.recordingServer.send(msg.serialize());
    };

    private createRecordingSocket = (session: IInitAuthSession, resolve: (value: any) => void) => {
        this.recordingServer = new SotiWebSocket(this.mcrcLog);
        const handler = new MessageHandler(null, this.messageHandler);
        handler.setOnConnect(() => {
            this.mcrcLog.info('Recording service connected');
            this.initRecordingServer(session);
            this.connectionPromise = null;
            resolve(null);
        });
        handler.setOnClose(() => {
            console.log('Recording service disconnected');
            this.recordingServer = null;
        });
        handler.setOnError(() => {
            this.recordingServer = null;
        });

        this.recordingServer.messageHandler = handler;

        this.mcrcLog.info('Connecting recording service: ' + this.recordingServiceUri);
        this.recordingServer.connect(this.recordingServiceUri, "mobicontrol-rc-v1", true);
    }

    private initRecordingServer = (session: IInitAuthSession) => {
        // recording server for streaming RC devices needs the ticket ID in the PC_INIT in order
        // to assign activities correctly
        const packet: IInitRequestPacket = {
            deviceId: RcContext.instance.deviceId,
            userName: RcContext.instance.userName,
            token: session.token,
            sessionId: session.sessionId,
            keys: ConnectionBridge.constructInitKeyString(true, 0, RcContext.instance.ticketId, RcContext.instance.deviceName),
            host: RcContext.instance.host,
            versionHigh: 0,
            versionLow: 0,
            sotiAssistDeviceId: RcContext.instance.sotiAssistDeviceId,
            forceOverride: false,
            disableUserDisplay: RcContext.instance.disableUserDisplay,
            disableUserInteraction: RcContext.instance.disableUserInteraction
        };
        const message = new StartRemoteControlMessage(packet);

        this.mcrcLog.info(
            'Creating PC_INIT - deviceId:' + message.request.deviceId
            + ' sotiAssistDeviceId:' + message.request.sotiAssistDeviceId
            + ' userName:' + message.request.userName
            + ' token:' + (message.request.token ? message.request.token.substr(0, 8) : '') + '...'
            + ' sessionId:' + (message.request.sessionId ? message.request.sessionId.substr(0, 10) : '') + '...'
            + ' keys: ' + (message.request.keys ? message.request.keys.substr(0, 50) : '') + '...'
            + ' versionHi:' + message.request.versionHigh
            + ' versionLo:' + message.request.versionLow
            + ' disableUserDisplay:' + message.request.disableUserDisplay
            + ' disableUserInteraction:' + message.request.disableUserInteraction
            + ' forceOverride:' + message.request.forceOverride);

        this.recordingServer.send(message.serialize());
        if (this.recordingSkinConfiguration) {
            this.recordingServer.send(this.recordingSkinConfiguration.serialize());
        }
    }

    public close = () => {
        if (this.socketInst) {
            this.socketInst.close(1000, "User ended session");
            this.socketInst = null;
        }

        if (this.recordingServer) {
            this.recordingServer.close(1000, '');
            this.recordingServer = null;
            this.recordingSkinConfiguration = null;
        }
    };

    public isConnected = () => this.socketInst ? this.socketInst.isConnected() : false;

    public getSocket = (): BaseSocket => this.socketInst;

    public setLastError = (error: number) => this.lastError = error;

    public getLastError = (): number => this.lastError;
}
